import { apiFetch } from '../services/api'

/**
 * Batch-6 referral attribution — the client half of the "Give a month, get a
 * month" funnel.
 *
 * The `?ref=<code>` param only ever reaches the BROWSER (a `/j/<code>` link
 * 302s to `/sign-up?ref=`), never Clerk's server-to-server webhook. So the
 * flow is: stash the code pre-auth, then once the account exists POST it to
 * /api/referrals/claim exactly once. The server is idempotent and one-way
 * (attributes only a <7-day-old, not-yet-attributed account, never self), so
 * an invalid/stale/duplicate code is silently ignored.
 */

const PENDING_REF_KEY = 'cf:pending-ref'

/** Once-per-load guard so a re-render / auth flap can't double-POST. */
let claimAttempted = false

/**
 * Stash a referral code seen pre-auth (called on the sign-up route). Never
 * throws — a blocked localStorage just means no attribution, which is fine.
 */
export function stashPendingReferral(code: string | null | undefined): void {
  if (!code || !code.trim()) return
  try {
    window.localStorage.setItem(PENDING_REF_KEY, code.trim())
  } catch {
    /* private mode / disabled storage — attribution is best-effort */
  }
}

/**
 * Claim any stashed referral once the user is authenticated. Clears the stash
 * up-front so a failure can't loop, and swallows everything — attribution must
 * never block or surface in the UI. Safe to call on every post-auth boot.
 */
export async function claimPendingReferral(): Promise<void> {
  if (claimAttempted) return
  claimAttempted = true

  let code: string | null = null
  try {
    code = window.localStorage.getItem(PENDING_REF_KEY)
    if (code) window.localStorage.removeItem(PENDING_REF_KEY)
  } catch {
    return
  }
  if (!code || !code.trim()) return

  try {
    await apiFetch('/referrals/claim', {
      method: 'POST',
      body: JSON.stringify({ code: code.trim() }),
    })
  } catch {
    /* network / server hiccup — the code is already cleared; never retried */
  }
}

/** Test-only: reset the once-per-load guard. */
export function __resetClaimGuardForTests(): void {
  claimAttempted = false
}
